import { Fragment, useEffect, useState } from "react";

import { getEditorDefaults } from "@pqina/pintura";
import { PinturaEditor } from "@pqina/react-pintura";
import { useQueryClient } from "@tanstack/react-query";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import Modal from "../../layouts/Modal";
import LoadingWheel from "../shared/LoadingWheel";
import { PartImageThumb } from "./PartImageThumb";
import { PartVideoThumb } from "./PartVideoThumb";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import FileUpload from "../shared/Inputs/FileUpload";
import { VehicleImageThumb } from "./VehicleImageThumb";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useVehicleImages from "../../data/useVehicleImages";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IPartImagePatchRequest } from "./../../models/Part";
import useNotification from "../notifications/useNotifications";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPart, IPartImage, IPartImageType, PartImageRequirementType } from "../../models/Part";
import usePartImageSettings from "../../data/usePartImageSettings";

import "@pqina/pintura/pintura.css";

export default function EditListingImages({ part, onlyPartImages = false }: { part: IPart, onlyPartImages?: boolean }) {
  const queryClient = useQueryClient();
  const { uploadPicture, autoReorderImages, patchPicture, removeImage, removePictureBg } = useSinglePartMutations(part.id);
  const { vehicleImages } = useVehicleImages(part.vehicleId!);
  const { saveHandlers } = useDefaultCRUDHandlers("Images Reordered");
  const { deleteHandlers } = useDefaultCRUDHandlers("Image");
  const { addNotification } = useNotification();
  const { apply: applyImageSettings } = usePartImageSettings();

  const [uploadIsLoading, setUploadIsLoading] = useState(false);
  const [isReorderingLoading, setIsReorderingLoading] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [newOrder, setNewOrder] = useState(0);
  const [editImageOpen, setEditImageOpen] = useState(false);
  const [editImage, setEditImage] = useState<Blob | undefined>(undefined);
  const [editImageId, setEditImageId] = useState<string | undefined>(undefined);
  const [editImageLoading, setEditImageLoading] = useState(false);
  const [editImageError, setEditImageError] = useState(false);
  const [toUpload, setToUpload] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [reorderDraft, setReorderDraft] = useState<Record<string, number | null>>({});

  const updateToUploadFiles = (files: any) => setToUpload({ ...files });

  const imageTypeOptions = Object.keys(IPartImageType).filter(key => isNaN(Number(key)));

  const closeEditImage = () => {
    setEditImageOpen(false);
    setTimeout(() => setEditImage(undefined), 200);
  };

  const handleUploadDocs = (file?: File, imageType?: PartImageRequirementType) => {
    //attach files to http request
    setUploadIsLoading(true);
    const formData = new FormData();

    if (!file) {
      for (let i = 0; i < Object.keys(toUpload).length; i++) {
        formData.append(part.id!, toUpload[i]);
      }
    } else {
      const editedFile = new File([file], `${Math.floor(Date.now() / 100)}-edited.jpeg`, { type: file.type });
      formData.append(part.id!, editedFile);
    }

    uploadPicture.mutate({
      data: formData,
      imageType: imageType
    }, {
      onSuccess: () => {
        setToUpload([]);
        editImageOpen && closeEditImage();
      },
      onError: (error) => {
        console.log(error);
      },
      onSettled: () => {
        setUploadIsLoading(false);
      },
    });
  };

  const handleStartStopReorder = () => {
    if (isReordering) {
      setIsReorderingLoading(true);
      //save new order
      // part patch
      let patchReqs: IPartImagePatchRequest[] = [];

      part.images.forEach((img) => {
        const draftOrder = reorderDraft[img.id!] ?? null;
        patchReqs.push({
          imageId: img.id!,
          order: draftOrder,
          isThumbnail: draftOrder === 0 ? true : false,
          useOnEbay: draftOrder !== null ? true : false,
        });
      });

      patchPicture.mutate(patchReqs, {
        onSuccess: () => {
          setIsReordering(false);
          setReorderDraft({});
          setNewOrder(0);
          saveHandlers.onSuccess();
        },
        onError: (error) => {
          console.log(error);
          saveHandlers.onError();
        },
        onSettled: () => {
          setIsReorderingLoading(false);
        }
      });
    } else {
      //start reordering
      // Keep a local draft order state so we don't mutate props/query cache objects.
      const initialDraft: Record<string, number | null> = {};
      part.images.forEach((img) => {
        if (img.id) {
          initialDraft[img.id] = null;
        }
      });
      setReorderDraft(initialDraft);
      setNewOrder(0);
      setIsReordering(true);
    }
  };

  const handleCancelReorder = () => {
    setIsReordering(false);
    setReorderDraft({});
    setNewOrder(0);
  };

  const handleAutoReorderImages = () => {

    const id = [part.id];

    autoReorderImages.mutate(id, {
      onSuccess: () => {
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      }
    });
  }

  const handleImageTypeChange = (type: IPartImageType) => {
    if (!editImageId) return;

    let body: IPartImagePatchRequest[] = [];
    body.push({
      imageId: editImageId,
      imageType: type,
    });

    patchPicture.mutate(body, saveHandlers);
  };

  const nonVideoImages = part.images?.filter((image) => !image.url?.includes('mp4')) ?? [];

  const loadEditImage = async (imgId: string) => {
    editImageError && setEditImageError(false);
    setEditImageLoading(true);
    setEditImageOpen(true);

    let imgUrl = part.images.find((img) => img.id === imgId)?.url!;

    try {
      const res = await fetch(
        //Use CORS proxy to get around CORS issue
        `https://arcane-peak-78372.herokuapp.com/${imgUrl}`,
        {
          method: "GET",
          headers: {
            "Access-Control-Allow-Origin": "*", // Required for CORS support to work
          },
        }
      );

      if (!res.ok) {
        throw Error(res.statusText);
      }

      const blob = await res.blob();

      setEditImage(blob);
      setEditImageId(imgId);
    } catch (e) {
      console.error(e);
      setEditImageError(true);
    } finally {
      setEditImageLoading(false);
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const handleOpenPreview = (imgId: string) => {
    const idx = nonVideoImages.findIndex((img) => img.id === imgId);
    setPreviewIndex(idx >= 0 ? idx : 0);
    setPreviewOpen(true);
  };

  const handleMovePreview = (direction: string) => {
    if (direction === "prev" && previewIndex > 0) {
      setPreviewIndex((prev) => prev - 1);
    } else if (direction === "next" && previewIndex < nonVideoImages.length - 1) {
      setPreviewIndex((prev) => prev + 1);
    }
  };

  const previewDisabled = (direction: string) => {
    if (direction === "prev") return previewIndex <= 0;
    if (direction === "next") return previewIndex >= nonVideoImages.length - 1;
    return false;
  };

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (previewOpen) {
        if (event.key === "ArrowLeft") {
          handleMovePreview("prev");
        } else if (event.key === "ArrowRight") {
          handleMovePreview("next");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewOpen, previewIndex]);

  const handleReorderClick = (imgId: string) => {
    const imageOrder = reorderDraft[imgId];
    // Stops order going up forever.
    if (imageOrder === null) {
      setReorderDraft((current) => ({ ...current, [imgId]: newOrder }));
      setNewOrder((current) => current + 1);
    }
  };

  const handleSetDeleteOpen = (imgId: string) => {
    setDeleteId(imgId);
    setDeleteOpen(true);
  };

  const handleRemoveImage = () => {
    if (!deleteId) return;

    removeImage.mutate(deleteId, {
      ...deleteHandlers,
      onSettled: () => {
        setDeleteOpen(false);
      }
    });
  };

  useEffect(() => { }, [part.images]);

  // console.log(vehicleImages.data);

  const handleBackgroundRemove = () => {
    setEditImageLoading(true);
    removePictureBg.mutate(editImageId, {
      onSuccess: () => {
        setEditImageOpen(false);
        addNotification({
          variant: "success",
          primaryText: "Background Removed",
        });
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Unable to remove background",
        });
      },
      onSettled: () => {
        setEditImageLoading(false);
      }
    });
  }

  const handleApplyImageSettings = () => {
    if (!part.id) return;

    applyImageSettings.mutate([part.id], {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Image settings applied successfully",
        });
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to apply image settings",
        });
      }
    });
  }

  const editorDefaults = getEditorDefaults({
    imageWriter: {
      quality: 0.8,
      mimeType: "image/jpeg"
    }
  });

  return (
    <Fragment key={part.id}>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title={"Delete Part Image?"}
        message={`Are you sure you want to delete this image?`}
        confirmButtonText={"Delete"}
        onConfirm={handleRemoveImage}
        isLoading={false}
      />
      <Modal
        open={editImageOpen}
        setOpen={setEditImageOpen}
        width={"max-w-7xl"}
      >
        {editImageOpen && (
          <div className="h-[80vh]">
            {editImageLoading ? (
              <div className="h-full flex items-center">
                <LoadingWheel />
              </div>
            ) : editImageError ? (
              <div className="h-full flex items-center">
                <p>Error loading image</p>
              </div>
            ) : (
              <div className="h-[80vh]">
                <PinturaEditor
                  {...editorDefaults}
                  src={editImage}
                  onProcess={(res) => handleUploadDocs(res.dest)}
                  utils={["crop", "finetune", "annotate", "redact"]}
                />
                <div className="flex gap-x-4 left-0 bottom-0 absolute m-8 z-10 ">
                  <ButtonInput
                    isSubmit={false}
                    onClick={handleBackgroundRemove}
                    classes={"text-black rounded-xl !border !border-solid !border-gray-200 hover:!border-gray-300"}
                  >
                    <div>
                      <FontAwesomeIcon icon={faImage} />
                      <p className={"text-xs"}>Remove<br />Background</p>
                    </div>
                  </ButtonInput>

                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600">Image Type</label>
                    <select
                      className="text-sm"
                      defaultValue={part.images.find(img => img.id == editImageId)?.imageType ?? ""}
                      onChange={(e) => handleImageTypeChange(Number(e.target.value) as IPartImageType)}
                    >
                      <option value="">None</option>
                      {imageTypeOptions.map((key, index) => (
                        <option key={index} value={IPartImageType[key as any]}>
                          {key}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <ButtonInput
                  isSubmit={false}
                  label="Close"
                  onClick={editImageOpen && closeEditImage}
                  classes={"right-0 bottom-0 absolute m-8 z-10 bg-gray-400 hover:bg-gray-500 text-black rounded-xl !border !border-solid !border-gray-200 hover:!border-gray-300"}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
      <Modal open={previewOpen} setOpen={setPreviewOpen} width="max-w-4xl">
        {previewOpen && nonVideoImages.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleMovePreview("prev")}
              className="shrink-0 p-1 text-black opacity-75 hover:opacity-100 disabled:opacity-25 disabled:cursor-not-allowed transition-all ease-in-out duration-300"
              disabled={previewDisabled("prev")}
            >
              <ChevronLeftIcon className="w-10 h-10" />
              <span className="sr-only">Prev</span>
            </button>
            <div className="flex-1 min-w-0 flex justify-center">
              <img src={nonVideoImages[previewIndex]?.url} alt="Preview" className="w-full" />
            </div>
            <button
              onClick={() => handleMovePreview("next")}
              className="shrink-0 p-1 text-black opacity-75 hover:opacity-100 disabled:opacity-25 disabled:cursor-not-allowed transition-all ease-in-out duration-300"
              disabled={previewDisabled("next")}
            >
              <ChevronRightIcon className="w-10 h-10" />
              <span className="sr-only">Next</span>
            </button>
          </div>
        )}
      </Modal>

      <Card title="Part Images" bodyClassName="p-4">
        {isReorderingLoading ? (
          <div>
            <LoadingWheel />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 2xl:grid-cols-5">
              {part.images?.length > 0 ? (
                part.images.filter((image) => !image.url?.includes('mp4')).map((image: IPartImage, i: number) => (
                  <PartImageThumb
                    key={image.id}
                    image={isReordering ? { ...image, order: reorderDraft[image.id!] ?? null } : image}
                    index={i}
                    handleEdit={loadEditImage}
                    handleReorder={handleReorderClick}
                    handleRemove={handleSetDeleteOpen}
                    handlePreview={handleOpenPreview}
                    reordering={isReordering}
                  />
                ))
              ) : (
                <p className="text-center w-full py-4">No images found!</p>
              )}
            </div>

            <div className="flex gap-x-4 mt-4">
              {isReordering && <p className="text-center mt-2">Click on images to set order</p>}
              <ButtonInput
                label={isReordering ? "Save Order" : "Reorder"}
                isSubmit={false}
                onClick={handleStartStopReorder}
                disabled={part.images?.length < 1 || part.soldDate !== null}
                classes={""}
              />
              {isReordering && (
                <ButtonInput
                  label="Cancel"
                  isSubmit={false}
                  onClick={handleCancelReorder}
                  classes={"bg-gray-400 hover:bg-gray-500 text-black"}
                />
              )}
              {!isReordering && (
                <>
                  <ButtonInput
                    label="Auto Reorder"
                    isSubmit={false}
                    onClick={handleAutoReorderImages}
                    isLoading={autoReorderImages.isLoading}
                    classes={""}
                  />
                  <ButtonInput
                    label="Apply Image Settings"
                    isSubmit={false}
                    onClick={handleApplyImageSettings}
                    disabled={part.soldDate !== null}
                    isLoading={applyImageSettings.isLoading}
                    classes={""}
                  />
                </>
              )}
            </div>
          </>
        )}
      </Card >

      {!onlyPartImages && (

        <>
          <div className="my-4" />

          <Card title="Part Videos" bodyClassName="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 2xl:grid-cols-5">
              {part.images?.length > 0 ? (
                part.images.filter((image) => image.url?.includes('mp4')).map((video: IPartImage, i: number) => (
                  <PartVideoThumb
                    video={video}
                    index={i}
                    handleRemove={handleSetDeleteOpen}
                  />
                ))
              ) : (
                <p className="text-center w-full py-4">No videos found!</p>
              )}
            </div>
          </Card>

          <div className="my-4" />

          <Card title="Vehicle Images" bodyClassName="p-4">
            {vehicleImages.isLoading ? (
              <LoadingWheel />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 2xl:grid-cols-5">
                {vehicleImages.data
                  ?.filter((image) => !image.contentType?.includes("video"))
                  .filter((image) => image.thumbnail)
                  .map((image, i) => (
                    <VehicleImageThumb image={image} index={i} onClick={(file) => handleUploadDocs(file, PartImageRequirementType.Vehicle)} />
                  ))}
              </div>
            )}
          </Card>

          <div className="mt-4">
            {uploadIsLoading ? (
              <div className="flex items-center py-[10vh]">
                <LoadingWheel />
              </div>
            ) : (
              <>
                <FileUpload
                  label={"Upload Part Photos here"}
                  accept=".jpg,.png,.jpeg"
                  desc="Add pictures to this part..."
                  multiple
                  updateFilesCb={updateToUploadFiles}
                />
                <ButtonInput
                  classes=""
                  label="Upload"
                  isSubmit={false}
                  onClick={() => handleUploadDocs()}
                  disabled={toUpload.length < 1}
                />
              </>
            )}
          </div>
        </>
      )}
    </Fragment>
  );
}
